import React, { useState } from 'react';
import { initiateCheckout } from '../services/stripeService';
import { useAuth } from '../contexts/AuthContext';
import {
  PRICING_TIERS,
  getSavingsLabel,
  type BillingPeriod,
} from '../config/pricing';

interface UpgradeModalProps {
  isOpen: boolean;
  onClose: () => void;
  initialMode?: 'login' | 'signup';
}

const modalFont = { fontFamily: "'Sora', ui-sans-serif, system-ui, sans-serif" };

const UpgradeModal: React.FC<UpgradeModalProps> = ({ isOpen, onClose }) => {
  const { user } = useAuth();
  const [loading, setLoading] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [billingPeriod, setBillingPeriod] = useState<BillingPeriod>('monthly');

  if (!isOpen) return null;

  const handlePurchase = async (plan: 'day_pass' | 'monthly_pro' | 'annual_pro') => {
    if (!user) {
      setError('Please sign in to purchase a plan.');
      return;
    }
    setLoading(plan);
    setError(null);

    try {
      await initiateCheckout(plan);
    } catch (err) {
      console.error('Checkout error:', err);
      setError('Failed to start checkout. Please try again.');
      setLoading(null);
    }
  };

  const dayPassTier = PRICING_TIERS.find((t) => t.id === 'day_pass')!;
  const proTier = PRICING_TIERS.find((t) => t.id === 'pro_monthly')!;
  const isProMonthlyPromo = billingPeriod === 'monthly';
  const annualSaveLabel = getSavingsLabel(proTier, 'annual');

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div
        className="absolute inset-0 bg-black/60 backdrop-blur-sm"
        onClick={onClose}
      />

      <div
        className="relative bg-white dark:bg-slate-800 rounded-2xl shadow-2xl max-w-lg w-full mx-4 overflow-hidden"
        style={modalFont}
      >
        <div className="bg-gradient-to-r from-emerald-500 to-teal-600 px-6 py-5">
          <h2 className="text-2xl font-bold text-white">Upgrade Your Plan</h2>
          <p className="text-emerald-100 mt-1">
            Unlock unlimited document preparation for AI workflows
          </p>
        </div>

        <div className="px-6 pt-5">
          <div className="flex justify-center">
            <div className="inline-flex items-center rounded-full bg-slate-100 dark:bg-slate-700 p-1">
              <button
                type="button"
                onClick={() => setBillingPeriod('monthly')}
                className={`px-4 py-1.5 text-sm font-medium rounded-full transition-all ${
                  billingPeriod === 'monthly'
                    ? 'bg-white dark:bg-slate-600 text-slate-900 dark:text-white shadow-sm'
                    : 'text-slate-600 dark:text-slate-400'
                }`}
              >
                Monthly
              </button>
              <button
                type="button"
                onClick={() => setBillingPeriod('annual')}
                className={`px-4 py-1.5 text-sm font-medium rounded-full transition-all ${
                  billingPeriod === 'annual'
                    ? 'bg-white dark:bg-slate-600 text-slate-900 dark:text-white shadow-sm'
                    : 'text-slate-600 dark:text-slate-400'
                }`}
              >
                Annual
                {annualSaveLabel && (
                  <span className="ml-1 text-xs text-emerald-600 dark:text-emerald-400">
                    {annualSaveLabel}
                  </span>
                )}
              </button>
            </div>
          </div>
        </div>

        <div className="p-6 space-y-4">
          {error && (
            <div className="p-3 bg-red-100 dark:bg-red-900/30 border border-red-200 dark:border-red-700 rounded-lg text-red-700 dark:text-red-300 text-sm">
              {error}
            </div>
          )}

          <div className="border-2 border-slate-200 dark:border-slate-600 rounded-xl p-5 hover:border-emerald-400 dark:hover:border-emerald-500 transition-colors">
            <div className="flex justify-between items-start mb-3">
              <div>
                <h3 className="text-lg font-semibold text-slate-800 dark:text-white">
                  {dayPassTier.name}
                </h3>
                <p className="text-slate-500 dark:text-slate-400 text-sm">
                  {dayPassTier.tagline}
                </p>
              </div>
              <div className="text-right">
                <span className="text-2xl font-bold text-emerald-600 dark:text-emerald-400">
                  ${dayPassTier.monthlyPrice}
                </span>
                <span className="text-slate-500 dark:text-slate-400 text-sm">/ 24 hours</span>
              </div>
            </div>
            <ul className="text-sm text-slate-600 dark:text-slate-300 space-y-1 mb-4">
              {dayPassTier.features.map((feature, i) => (
                <li key={i} className="flex items-center gap-2">
                  <svg
                    className="w-4 h-4 text-emerald-500"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M5 13l4 4L19 7"
                    />
                  </svg>
                  {feature}
                </li>
              ))}
            </ul>
            <button
              type="button"
              onClick={() => handlePurchase('day_pass')}
              disabled={loading !== null}
              className="w-full py-2.5 px-4 bg-slate-800 dark:bg-slate-700 text-white font-medium rounded-lg hover:bg-slate-700 dark:hover:bg-slate-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading === 'day_pass' ? (
                <span className="flex items-center justify-center gap-2">
                  <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24">
                    <circle
                      className="opacity-25"
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      strokeWidth="4"
                      fill="none"
                    />
                    <path
                      className="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
                    />
                  </svg>
                  Processing...
                </span>
              ) : (
                'Get 24-Hour Access'
              )}
            </button>
          </div>

          <div className="flex flex-col gap-2">
            {isProMonthlyPromo && (
              <div className="flex justify-center">
                <span
                  className="inline-block rounded-full px-3 py-1 font-semibold"
                  style={{
                    backgroundColor: '#cf9738',
                    color: '#15452f',
                    fontSize: '11px',
                    fontFamily: "'Sora', sans-serif",
                  }}
                >
                  Early Adopter Price
                </span>
              </div>
            )}
            <div className="rounded-xl p-5 relative bg-emerald-50/50 dark:bg-emerald-900/10 border-[1.5px] border-solid border-[#cf9738]">
            <div className="flex justify-between items-start mb-3">
              <div>
                <h3 className="text-lg font-semibold text-slate-800 dark:text-white">
                  {proTier.name}
                </h3>
                <p className="text-slate-500 dark:text-slate-400 text-sm">
                  {proTier.tagline}
                </p>
              </div>
              <div className="text-right">
                {isProMonthlyPromo && proTier.listMonthlyPrice != null ? (
                  <div>
                    <div className="flex flex-wrap items-baseline justify-end gap-x-1.5 gap-y-0">
                      <span
                        className="text-sm font-medium"
                        style={{ color: '#888888', textDecoration: 'line-through' }}
                      >
                        ${proTier.listMonthlyPrice?.toFixed(2)}/month
                      </span>
                      <span className="text-2xl font-bold text-emerald-600 dark:text-emerald-400">
                        ${proTier.monthlyPrice?.toFixed(2)}/month
                      </span>
                    </div>
                    <p className="mt-1 text-xs text-slate-500 dark:text-slate-400 text-right max-w-[14rem] ml-auto">
                      Locks in your rate before we raise pricing
                    </p>
                  </div>
                ) : billingPeriod === 'annual' &&
                  proTier.annualPrice != null &&
                  proTier.monthlyPrice != null ? (
                  <div>
                    <div className="flex flex-wrap items-baseline justify-end gap-x-1.5 gap-y-0">
                      <span
                        className="text-sm font-medium"
                        style={{ color: '#888888', textDecoration: 'line-through' }}
                      >
                        ${(proTier.monthlyPrice * 12).toFixed(2)}
                      </span>
                      <span className="text-2xl font-bold text-emerald-600 dark:text-emerald-400">
                        ${proTier.annualPrice}
                      </span>
                      <span className="text-slate-500 dark:text-slate-400 text-sm">/ year</span>
                    </div>
                    {annualSaveLabel && (
                      <p className="mt-1 text-xs font-medium text-emerald-600 dark:text-emerald-400 text-right">
                        {annualSaveLabel}
                      </p>
                    )}
                    <p className="mt-1 text-xs text-slate-500 dark:text-slate-400 text-right max-w-[14rem] ml-auto">
                      ${(proTier.annualPrice / 12).toFixed(2)}/mo equivalent — billed yearly
                    </p>
                  </div>
                ) : (
                  <>
                    <span className="text-2xl font-bold text-emerald-600 dark:text-emerald-400">
                      ${proTier.monthlyPrice}
                    </span>
                    <span className="text-slate-500 dark:text-slate-400 text-sm">/ month</span>
                  </>
                )}
              </div>
            </div>
            <ul className="text-sm text-slate-600 dark:text-slate-300 space-y-1 mb-4">
              {proTier.features.map((feature, i) => (
                <li key={i} className="flex items-center gap-2">
                  <svg
                    className="w-4 h-4 text-emerald-500"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M5 13l4 4L19 7"
                    />
                  </svg>
                  {feature}
                </li>
              ))}
            </ul>
            <button
              type="button"
              onClick={() =>
                handlePurchase(billingPeriod === 'annual' ? 'annual_pro' : 'monthly_pro')
              }
              disabled={loading !== null}
              className="w-full py-2.5 px-4 text-white font-medium rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed hover:opacity-95"
              style={{ backgroundColor: 'var(--tli-green, #15452f)' }}
            >
              {loading === 'monthly_pro' || loading === 'annual_pro' ? (
                <span className="flex items-center justify-center gap-2">
                  <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24">
                    <circle
                      className="opacity-25"
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      strokeWidth="4"
                      fill="none"
                    />
                    <path
                      className="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
                    />
                  </svg>
                  Processing...
                </span>
              ) : billingPeriod === 'annual' ? (
                'Subscribe Annually'
              ) : (
                'Try Pro Free for 7 Days'
              )}
            </button>
            {isProMonthlyPromo && (
              <p className="mt-3 text-center text-sm text-slate-600 dark:text-slate-400">
                Then $9.99/month. Cancel anytime.
              </p>
            )}
            </div>
          </div>
        </div>

        <div className="px-6 py-4 bg-slate-50 dark:bg-slate-900/50 border-t border-slate-200 dark:border-slate-700">
          <button
            type="button"
            onClick={onClose}
            className="w-full py-2 text-slate-600 dark:text-slate-400 hover:text-slate-800 dark:hover:text-slate-200 font-medium transition-colors"
          >
            Maybe Later
          </button>
        </div>

        <button
          type="button"
          onClick={onClose}
          className="absolute top-4 right-4 text-white/80 hover:text-white"
          aria-label="Close"
        >
          <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      </div>
    </div>
  );
};

export default UpgradeModal;
